import classNames from "classnames"
import Cookies from "js-cookie"
import ApplicationComponent from "legacy/ApplicationComponent"
import AssignmentSearchForm from "legacy/assignments-v2/AssignmentSearchForm"
import AssignmentsDateButtons from "legacy/assignments-v2/AssignmentsDateButtons"
import AssignmentsPopover from "legacy/assignments-v2/AssignmentsPopover"
import { highlightSpace } from "legacy/assignments-v2/highlightSpace"
import {
  boatDisplayText,
  capitalizeFirstLetter,
  inchesToFeet,
  isPresent,
} from "legacy/helpers.es6"
import _ from "lodash"
import moment from "moment"
import PropTypes from "prop-types"
import React from "react"

import Badge from "src/components/Badge"
import {
  statusBadgeClassName,
  statusToneClassName,
} from "src/components/Badge/status_tokens"

class AssignmentsView extends ApplicationComponent {
  constructor(props) {
    super(props)
    this.state = {
      collapsedSpaceGroups: Cookies.get("collapsedSpaceGroups")
        ? JSON.parse(Cookies.get("collapsedSpaceGroups"))
        : [],
      loading: true,
    }
    this.leftGutterWidth = 130
    this.popoverDelayHandler = {}
    this.toggleAllSpaceGroups = this.toggleAllSpaceGroups.bind(this)
    this.expandSpaceGroupForSpace = this.expandSpaceGroupForSpace.bind(this)
    this.moveForward = this.moveForward.bind(this)
    this.moveBackward = this.moveBackward.bind(this)
    this.clearPopover = this.clearPopover.bind(this)
  }

  calculateRemainingFeet(day, spaceGroup) {
    if (isPresent(this.props.loaV2Availability)) {
      const data = this.props.loaV2Availability[spaceGroup.id]
      if (!isNaN(data[day])) {
        return `${inchesToFeet(data[day])}'`
      }
    }
  }

  layoutGridCols() {
    if (this.isDockwalkView()) {
      return {
        gridTemplateColumns:
          "minmax(0,130px) minmax(0,130px) minmax(0,140px) minmax(0,88px) 1fr",
      }
    }

    return {
      gridTemplateColumns: "repeat(2,minmax(0,130px)) minmax(0,140px) 1fr",
    }
  }

  moveForward() {
    const date = this.props.visibleRange.start
      .clone()
      .add(this.props.viewTypeInDays, "days")
    this.props.dateChange(date)
  }

  moveBackward() {
    const date = this.props.visibleRange.start
      .clone()
      .subtract(this.props.viewTypeInDays, "days")
    this.props.dateChange(date)
  }

  isDockwalkView() {
    return this.props.viewType === "dockwalk"
  }

  gridColumns() {
    return this.props.viewTypeInDays * 2
  }

  gridEdges() {
    return this.gridColumns() + 1
  }

  gridColumnClass() {
    switch (this.gridColumns()) {
      case 2:
        return "grid-cols-2"
      case 14:
        return "grid-cols-14"
      case 56:
        return "grid-cols-56"
    }
  }

  toggleAllSpaceGroups() {
    const toggledData = this.areAllSpaceGroupsCollapsed()
      ? []
      : _.map(this.props.spacesBySpaceGroups, (spaceGroup) => spaceGroup.id)
    this.setSpaceGroupData(toggledData)
  }

  spaceGroupToggle(spaceGroupId) {
    const newArray = [...this.state.collapsedSpaceGroups]
    this.spaceGroupVisible(spaceGroupId)
      ? newArray.push(spaceGroupId)
      : _.remove(newArray, (name) => name === spaceGroupId)
    this.setSpaceGroupData(newArray)
  }

  expandSpaceGroupForSpace(spaceId) {
    const spaceGroup = this.props.spacesBySpaceGroups.find((sg) =>
      sg.spaces.some((s) => s.id === spaceId)
    )
    if (spaceGroup && !this.spaceGroupVisible(spaceGroup.id)) {
      this.spaceGroupToggle(spaceGroup.id)
      return true
    }
    return false
  }

  setPopover(type, data) {
    // delay the popover so it doesn't flash as you move your cursor across the screen
    this.popoverDelayHandler[`${type}-${data.reservation_id || data.id}`] =
      setTimeout(() => {
        this.setState({ popoverData: { type, data } })
      }, 150)
  }

  clearPopover(type, data) {
    clearTimeout(
      this.popoverDelayHandler[`${type}-${data.reservation_id || data.id}`]
    )
    this.popoverDelayHandler[`${type}-${data.reservation_id || data.id}`] = null
    this.setState({ popoverData: null })
  }

  setSpaceGroupData(data) {
    this.setState({ collapsedSpaceGroups: data })
    Cookies.set("collapsedSpaceGroups", JSON.stringify(data))
  }

  spaceGroupVisible(spaceGroupId) {
    return this.state.collapsedSpaceGroups.indexOf(spaceGroupId) === -1
  }

  areAllSpaceGroupsCollapsed() {
    return (
      this.state.collapsedSpaceGroups.length ===
      this.props.spacesBySpaceGroups.length
    )
  }

  gridOrderForReservation(assignmentOrder, columnStart, columnEnd) {
    assignmentOrder[columnStart] = assignmentOrder[columnStart] || []
    let gridOrder = assignmentOrder[columnStart].length
    if (gridOrder !== 0) {
      _.times(gridOrder, (index) => {
        if (assignmentOrder[columnStart].indexOf(index) === -1) {
          gridOrder = index
        }
      })
    }

    _.each(_.range(columnStart, columnEnd), (column) => {
      assignmentOrder[column] = assignmentOrder[column] || []
      assignmentOrder[column].push(gridOrder)
    })
    return gridOrder
  }

  gridOrderForLinearDockReservation(bowLocation, sternLocation) {
    return Math.min(bowLocation, sternLocation)
  }

  getClassAndColumnForStart(reservation) {
    const start = reservation.start_night
    const index = this.props.visibleRange.daysArray.indexOf(start) * 2
    if (this.isDayTrip(reservation)) {
      return { column: index + 1, class: "assignment__day-trip" }
    } else if (start === this.props.visibleRange.dayBeforeStart) {
      return { column: 1, class: "assignment__before-visible-range" }
    } else if (start === reservation.check_in_date) {
      return { column: index + 2, class: "assignment__check-in" }
    } else {
      return { column: index + 2, class: "assignment__unassigned-before" }
    }
  }

  getClassAndColumnForEnd(reservation) {
    const end = reservation.end_night
    const index = this.props.visibleRange.daysArray.indexOf(end) * 2
    if (this.isDayTrip(reservation)) {
      return { column: index + 3, class: "assignment__day-trip" }
    } else if (
      end === this.props.visibleRange.dayAfterEnd ||
      moment(end).add(1, "days").format("YYYY-MM-DD") ===
        this.props.visibleRange.dayAfterEnd
    ) {
      return {
        column: this.gridEdges(),
        class: "assignment__after-visible-range",
      }
    } else if (
      moment(end).add(1, "days").format("YYYY-MM-DD") ===
      reservation.check_out_date
    ) {
      return { column: index + 4, class: "assignment__check-out" }
    } else {
      return { column: index + 4, class: "assignment__unassigned-after" }
    }
  }

  placeholderClassAndColumnForStart(placeholder) {
    const placeholderStart = moment(placeholder.start_date)
    const start = moment.max(placeholderStart, this.props.visibleRange.start)
    const index =
      this.props.visibleRange.daysArray.indexOf(start.format("YYYY-MM-DD")) * 2
    if (placeholder.start_date === placeholder.end_date) {
      return { column: index + 1, class: "assignment__day-trip" }
    } else if (start === placeholderStart) {
      return { column: index + 2, class: "assignment__check-in" }
    } else {
      return { column: 1, class: "assignment__before-visible-range" }
    }
  }

  placeholderClassAndColumnForEnd(placeholder) {
    const placeholderEnd = moment(placeholder.end_date)
    const end = moment.min(placeholderEnd, this.props.visibleRange.end)
    const index =
      this.props.visibleRange.daysArray.indexOf(end.format("YYYY-MM-DD")) * 2
    if (placeholder.start_date === placeholder.end_date) {
      return { column: index + 3, class: "assignment__day-trip" }
    } else if (end === placeholderEnd) {
      return { column: index + 2, class: "assignment__check-out" }
    } else {
      return {
        column: this.gridEdges(),
        class: "assignment__after-visible-range",
      }
    }
  }

  isDayTrip(reservation) {
    return reservation.check_in_date === reservation.check_out_date
  }

  shouldSkipAssignmentRender(reservation) {
    if (
      this.isDayTrip(reservation) &&
      (reservation.end_night === this.props.visibleRange.dayBeforeStart ||
        reservation.start_night === this.props.visibleRange.dayAfterEnd)
    ) {
      return true
    } else if (
      reservation.start_night === this.props.visibleRange.dayAfterEnd
    ) {
      return true
    } else {
      return false
    }
  }

  conflictsForSpaceDate(space) {
    const assignments = this.props.assignmentsBySpace[space.id] || []
    const placeholders = this.props.placeholdersBySpace[space.id] || []
    const spacefulObjects = assignments.concat(placeholders)
    if (spacefulObjects.length <= 1) return {}

    const object = {}
    _.each(this.props.visibleRange.daysArray, (day) => {
      object[day] = []
      const momentDay = moment(day)
      _.each(spacefulObjects, (spacefulObject) => {
        if (spacefulObject.type === "placeholder") {
          const start = moment(spacefulObject.start_date)
          const end = moment(spacefulObject.end_date)
          if (start.isSame(momentDay, "day")) {
            object[day].push("start")
          } else if (end.clone().isSame(momentDay, "day")) {
            object[day].push("end")
          } else if (
            start.isSameOrBefore(momentDay, "day") &&
            end.clone().isSameOrAfter(momentDay, "day")
          ) {
            object[day].push("total")
          }
        } else {
          const start = moment(spacefulObject.start_night)
          const end = moment(spacefulObject.end_night)
          if (
            this.isDayTrip(spacefulObject) &&
            start.isSame(momentDay, "day") &&
            end.isSame(momentDay, "day")
          ) {
            object[day].push("total")
          } else if (start.isSame(momentDay, "day")) {
            object[day].push("start")
          } else if (end.clone().add(1, "days").isSame(momentDay, "day")) {
            object[day].push("end")
          } else if (
            start.isSameOrBefore(momentDay, "day") &&
            end.clone().add(1, "days").isSameOrAfter(momentDay, "day")
          ) {
            object[day].push("total")
          }
        }
      })
      object[day] = this.hasConflict(object[day])
    })
    return object
  }

  hasConflict(days) {
    if (days.length <= 1) return false

    // if it only has a start and an end, it's not a conflict
    return !(
      days.length === 2 &&
      days.includes("end") &&
      days.includes("start")
    )
  }

  isSelectedPlaceholder(placeholder) {
    return (
      this.props.selectedPlaceholder &&
      this.props.selectedPlaceholder.id === placeholder.id &&
      this.props.selectedPlaceholder.type === placeholder.type
    )
  }

  verticalDividers() {
    return _.map(_.times(this.gridColumns() / 2 - 1), (index) => {
      return (
        <div
          key={`vertical-divider-${index}`}
          className={`ml-auto h-full w-px bg-gray-100 col-start-${
            (index + 1) * 2
          } col-end-${(index + 1) * 2}`}
        />
      )
    })
  }

  unassignedReservationBadge(additionalClasses = "") {
    if (this.props.unassignedReservationsCount === 0) return null

    return (
      <div
        className={`badge-tone badge-tone-critical rounded-full ${additionalClasses}`}
      >
        {this.props.unassignedReservationsCount}
      </div>
    )
  }

  renderDates() {
    let index = -1
    return _.map(this.props.visibleRange.daysArray, (day) => {
      index++
      return (
        <div
          key={day}
          className={`hover:bg-gray cursor-pointer rounded text-center font-semibold col-start-${
            index * 2 + 1
          } col-end-${index * 2 + 3}`}
          onClick={() => this.props.dateChange(moment(day, "YYYY-MM-DD"))}
        >
          <div>{this.renderDateTitle(day)}</div>
          <div>{moment(day, "YYYY-MM-DD").format("D")}</div>
        </div>
      )
    })
  }

  renderAvailableFootage(spaceGroup) {
    let index = -1
    return (
      <div className="flex flex-grow flex-col items-center p-2">
        <div className={`grid w-full ${this.gridColumnClass()} mt-4`}>
          {_.map(this.props.visibleRange.daysArray, (day) => {
            index++
            return (
              <div
                key={day}
                className={`text-center font-semibold col-start-${
                  index * 2 + 1
                } col-end-${index * 2 + 3}`}
              >
                {this.props.loadingAvailability ? (
                  <h4 className="text-muted flex justify-center">
                    <i className="fa-solid fa-spinner-third fa-spin" />
                  </h4>
                ) : (
                  <span>{this.calculateRemainingFeet(day, spaceGroup)}</span>
                )}
              </div>
            )
          })}
        </div>
      </div>
    )
  }

  renderDateTitle(day) {
    if (this.isDockwalkView()) {
      return <span>{moment(day, "YYYY-MM-DD").format("dddd")}</span>
    } else {
      return (
        <div>
          <span className="xl:hidden">
            {moment(day, "YYYY-MM-DD").format("dd").slice(0, -1)}
          </span>
          <span className="hidden xl:inline">
            {moment(day, "YYYY-MM-DD").format("ddd")}
          </span>
        </div>
      )
    }
  }

  monthLabels() {
    const start = this.props.visibleRange.start
    const end = moment(this.props.visibleRange.end).startOf("month")
    const diff = end.diff(start, "days") * 2 + 1
    const html = []
    if (start.format("MMMM") !== end.format("MMMM")) {
      html.push(
        <div
          key="last-month"
          className={`truncate pl-2 pt-1 col-start-${diff} col-end-${this.gridEdges()}`}
        >
          {this.props.visibleRange.end.format("MMMM YYYY")}
        </div>
      )
    }
    html.unshift(
      <div
        key="first-month"
        className={`col-start-1 truncate pl-2 pt-1 col-end-${
          html.length ? diff : this.gridEdges()
        }`}
      >
        {this.props.visibleRange.start.format("MMMM YYYY")}
      </div>
    )
    return html
  }

  renderCollapseAllBtn() {
    return (
      <div
        className="btn btn-sm btn-tertiary"
        onClick={this.toggleAllSpaceGroups}
      >
        <span>
          {this.areAllSpaceGroupsCollapsed() ? "Expand All" : "Collapse All"}
        </span>
        <i
          className={classNames(
            "fa-solid fa-chevron-right transition duration-100",
            {
              "fa-rotate-90": !this.areAllSpaceGroupsCollapsed(),
            }
          )}
          style={{ fontSize: 20 }}
        />
      </div>
    )
  }

  renderAddButtonGroup() {
    return (
      <div className="btn-group md:hidden">
        <button
          type="button"
          className="btn btn-secondary dropdown-toggle relative"
          data-toggle="dropdown"
          aria-haspopup="true"
          aria-expanded="false"
        >
          Add
          <i className="fa-solid fa-circle-plus ml-2" />
        </button>
        <ul className="dropdown-menu dropdown-menu-right">
          <li>
            <a
              className="flex items-center py-2"
              href="#"
              onClick={this.props.openUnassignedReservations}
            >
              <i className="fa-solid fa-fw fa-clipboard-list mr-1" />
              Assign Reservations
              {this.unassignedReservationBadge("ml-1")}
            </a>
          </li>
          <li>
            <a
              className="py-2"
              href="#"
              onClick={this.props.openAddPlaceholder}
            >
              <i className="fa-solid fa-fw fa-thumbtack mr-1" />
              Add Placeholder
            </a>
          </li>
          <li>
            <a className="py-2" href="#" onClick={this.props.openAddNote}>
              <i className="fa-solid fa-fw fa-note-sticky mr-1" />
              Add Note
            </a>
          </li>
        </ul>
      </div>
    )
  }

  render() {
    return (
      <div className="rounded bg-white" style={{ fontSize: 12 }}>
        <div
          className="sticky top-0 z-20 md:top-18"
          style={{
            backgroundColor: "#F1F5F8",
            borderLeft: "1px solid #F1F5F8",
            borderRight: "1px solid #F1F5F8",
            margin: "0 -2px",
          }}
        >
          <div className="w-full rounded-t border bg-white">
            <div className="flex flex-col">
              <div className="flex flex-col p-2 md:flex-row md:justify-between">
                <AssignmentsDateButtons
                  dayIncrement={this.props.viewTypeInDays}
                  moveBackward={this.moveBackward}
                  moveForward={this.moveForward}
                  visibleRange={this.props.visibleRange}
                />
                <div className="flex w-full space-x-2 pt-2 md:w-2/5 md:space-x-0 md:pt-0">
                  <AssignmentSearchForm
                    closeSidebar={this.props.closeSidebar}
                    expandSpaceGroupForSpace={this.expandSpaceGroupForSpace}
                    searchFormData={this.props.searchFormData}
                    loadReservationForSidebar={
                      this.props.loadReservationForSidebar
                    }
                    setPlaceholder={this.props.setPlaceholder}
                    viewType={this.props.viewType}
                  />
                  {this.renderAddButtonGroup()}
                </div>
              </div>
              {!this.isDockwalkView() && (
                <div className="flex w-full flex-grow p-2">
                  <div
                    className="flex items-end"
                    style={{ width: this.leftGutterWidth }}
                  >
                    {this.renderCollapseAllBtn()}
                  </div>
                  <div className={`grid w-full ${this.gridColumnClass()}`}>
                    {this.renderDates()}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        {this.isDockwalkView()
          ? this.renderDockwalk()
          : this.renderSpaceGroups()}
        <AssignmentsPopover data={this.state.popoverData} />
      </div>
    )
  }

  renderDockwalk() {
    return (
      <div>
        <div
          className="bg-gray text-muted sticky z-10 grid border-b border-gray-200"
          style={{ top: 51, ...this.layoutGridCols() }}
        >
          <div className="px-3 py-2">Space</div>
          <div className="px-3 py-2">Status</div>
          <div className="px-3 py-2">Updated</div>
          <div className="px-3 py-2 text-center">Flag</div>
        </div>
        {this.renderSpaces(this.props.spacesForDockwalk)}
      </div>
    )
  }

  renderSpaceGroups() {
    return _.map(this.props.spacesBySpaceGroups, (spaceGroup) => {
      return (
        <div key={spaceGroup.id}>
          <div
            className="flex w-full bg-gray-100"
            style={{ borderBottom: "2px solid #eef2f6" }}
          >
            <div
              className="sticky z-10 flex cursor-pointer flex-col bg-gray-100 px-3 py-2"
              style={{ top: 87, width: this.leftGutterWidth }}
              onClick={this.spaceGroupToggle.bind(this, spaceGroup.id)}
            >
              <div
                className="text-muted mb-1 font-semibold"
                style={{ fontSize: 10 }}
              >
                {this.spaceGroupDisplayType()[spaceGroup.type]}
              </div>
              <div className="flex items-center">
                {spaceGroup.name}
                <i
                  className={classNames(
                    "fa-solid fa-chevron-right transition duration-100",
                    { "fa-rotate-90": this.spaceGroupVisible(spaceGroup.id) }
                  )}
                  style={{ fontSize: 20 }}
                />
              </div>
            </div>
            {spaceGroup.type === "SpaceGroup::LinearV2" &&
              this.props.viewType === "weekly" &&
              this.renderAvailableFootage(spaceGroup)}
          </div>
          {this.renderSpaceGroup(spaceGroup)}
        </div>
      )
    })
  }

  shouldNotRenderSpace(spaceId) {
    if (
      this.props.visibleSpaces === "with_assignments" &&
      !this.props.assignmentsBySpace[spaceId]
    ) {
      return true
    } else if (
      this.props.visibleSpaces === "without_assignments" &&
      this.props.assignmentsBySpace[spaceId]
    ) {
      return true
    } else {
      return false
    }
  }

  lastSpaceStatus(statuses) {
    if (statuses.length === 0) {
      return "unchecked"
    } else {
      const mostRecentStatusesFirst = _.reverse(
        _.sortBy(statuses, "created_at")
      )
      return mostRecentStatusesFirst[0].status
    }
  }

  lastSpaceStatusUpdated(statuses) {
    if (statuses.length === 0) {
      return null
    } else {
      const mostRecentStatusesFirst = _.reverse(
        _.sortBy(statuses, "created_at")
      )
      return mostRecentStatusesFirst[0].created_at
    }
  }

  formattedStatusUpdated(lastUpdated) {
    if (!lastUpdated) {
      return null
    } else if (
      moment(lastUpdated, moment.ISO_8601).format("L") ===
      this.props.visibleRange.start.format("L")
    ) {
      return moment.parseZone(lastUpdated, moment.ISO_8601).local().format("LT")
    } else {
      return moment
        .parseZone(lastUpdated, moment.ISO_8601)
        .local()
        .format("LT on L")
    }
  }

  historicalStatuses(statuses) {
    if (statuses.length <= 1) return null

    const mostRecentStatusesLast = _.sortBy(statuses, "created_at")
    return `
      <div class="space-y-2">
        ${_.map(
          mostRecentStatusesLast,
          (spaceStatus) => `
          <div class="flex justify-between space-x-2">
            <div class="${statusBadgeClassName(spaceStatus.status)}">
              ${capitalizeFirstLetter(spaceStatus.status)}
            </div>
            <div>${this.formattedStatusUpdated(spaceStatus.created_at)}</div>
          </div>
        `
        ).join("")}
      </div>
    `
  }

  flagIndicatorClass(flaggedCount) {
    if (flaggedCount > 0) {
      return "bg-red-50 text-red-400"
    }

    return "bg-gray-100 text-gray-300"
  }

  isDockwalkFlaggedNote(placeholder) {
    return (
      this.isDockwalkView() &&
      placeholder.type === "note" &&
      placeholder.is_flagged
    )
  }

  placeholderClasses(placeholder) {
    if (this.isDockwalkFlaggedNote(placeholder)) {
      return this.isSelectedPlaceholder(placeholder)
        ? "bg-red-100 text-red-600"
        : "bg-red-50 text-red-500"
    }

    if (placeholder.type === "note") {
      return this.isSelectedPlaceholder(placeholder)
        ? "bg-yellow-400 text-yellow-900"
        : "bg-yellow-200 text-yellow-900"
    }

    return this.isSelectedPlaceholder(placeholder)
      ? "bg-gray-400 text-gray-900"
      : "bg-gray-200 text-gray-900"
  }

  placeholderIconClass(placeholder) {
    if (this.isDockwalkFlaggedNote(placeholder)) {
      return "fa-flag"
    }

    return placeholder.type === "note" ? "fa-note-sticky" : "fa-thumbtack"
  }

  placeholderIconStyle() {
    return "fa-solid"
  }

  renderLeftAssignmentColumns(space, hasConflict) {
    if (this.isDockwalkView()) {
      const statuses = this.props.spaceStatusesBySpace[space.id] || []
      const flaggedCount = (
        this.props.spaceNotesBySpace[space.id] || []
      ).filter((spaceNote) => spaceNote.is_flagged).length
      return [
        <div
          key="space-name-1"
          className="flex items-center border-r border-gray-200 px-3 py-2"
        >
          {this.renderSpaceName(space.name, hasConflict)}
        </div>,
        <div
          key="space-name-2"
          className="flex items-center border-r border-gray-200 px-3 py-2"
        >
          <select
            className={`cursor-pointer rounded px-2 py-1 text-xs font-bold outline-none focus:ring ${statusToneClassName(
              this.lastSpaceStatus(statuses)
            )}`}
            style={{ WebKitAppearance: "none" }} // this removes the default webkit styling in (mobile) Safari. now they look like we designed them!
            name="status"
            value={this.lastSpaceStatus(statuses)}
            onChange={(event) =>
              this.props.addSpaceStatus(space.id, event.target.value)
            }
          >
            <option value="unchecked">Unchecked</option>
            <option value="vacant">Vacant</option>
            <option value="occupied">Occupied</option>
          </select>
        </div>,
        <div
          key="space-name-3"
          className="col-span-2 flex cursor-pointer items-center border-r border-gray-200 px-3 py-2 md:col-span-1"
          data-content={this.historicalStatuses(statuses)}
          data-container="body"
          data-toggle={statuses.length >= 2 ? "popover" : ""}
          data-placement="top"
          data-html="true"
        >
          {this.formattedStatusUpdated(this.lastSpaceStatusUpdated(statuses))}
          {statuses.length >= 2 ? (
            <span className="ml-2">
              <Badge color="neutral" text="log" />
            </span>
          ) : null}
        </div>,
        <div
          key="space-name-4"
          className="flex items-center justify-center gap-1 border-r border-gray-200 px-1 py-1"
        >
          <div
            className={`inline-flex min-w-[2.25rem] items-center justify-center gap-1 rounded-full px-2 py-1 text-xs font-semibold ${this.flagIndicatorClass(
              flaggedCount
            )}`}
            aria-label={
              flaggedCount > 0
                ? `${flaggedCount} flagged ${
                    flaggedCount === 1 ? "note" : "notes"
                  }`
                : "No flagged notes"
            }
          >
            <i className="fa-solid fa-flag text-base" />
          </div>
          <button
            type="button"
            className="btn btn-ghost-tertiary btn-sm btn-icon-only text-lg font-semibold text-blue-600"
            onClick={() => this.props.openNoteForSpace(space.id)}
            aria-label={`Add flagged note for ${space.name}`}
          >
            +
          </button>
        </div>,
      ]
    } else if (space.type === "SpaceGroup::LinearV2") {
      return (
        <div className="flex flex-col border-r border-gray-200 px-3 py-2">
          <span>{space.orientation_start} - 0&apos;</span>
          <span>↓</span>
          <span>
            {space.orientation_end} - {`${inchesToFeet(space.total)}'`}
          </span>
        </div>
      )
    } else {
      return (
        <div className="flex items-center border-r border-gray-200 px-3 py-2">
          {this.renderSpaceName(space.name, hasConflict)}
        </div>
      )
    }
  }

  renderSpaceGroup(spaceGroup) {
    if (this.spaceGroupVisible(spaceGroup.id)) {
      if (spaceGroup.type === "SpaceGroup::LinearV2") {
        return this.renderLoaV2Spaces(spaceGroup)
      } else {
        return this.renderSpaces(spaceGroup.spaces)
      }
    } else {
      return null
    }
  }

  renderLoaV2Spaces(spaceGroup) {
    return _.map(_.orderBy(spaceGroup.spaces), (space) => {
      if (this.shouldNotRenderSpace(space.id)) return null

      return (
        <div
          key={space.id}
          data-space-id={space.id}
          className="relative grid border-b border-gray-200"
          style={this.layoutGridCols()}
        >
          {this.renderLeftAssignmentColumns(spaceGroup)}
          <div
            className={`relative pt-1 ${
              this.isDockwalkView()
                ? "col-span-5 border-t border-gray-200 md:col-span-1 md:border-t-0"
                : "col-span-3"
            }`}
          >
            <div className={`grid ${this.gridColumnClass()} absolute inset-0`}>
              {this.verticalDividers()}
            </div>
            <div className={`grid ${this.gridColumnClass()} relative w-full`}>
              {this.renderAll(space)}
            </div>
          </div>
        </div>
      )
    })
  }

  renderSpaces(spaces) {
    return _.map(_.orderBy(spaces, ["order"], ["asc"]), (space) => {
      if (this.shouldNotRenderSpace(space.id)) return null
      if (this.isDockwalkView() && space.type === "SpaceGroup::LinearV2") {
        return (
          <div
            key={space.id}
            data-space-id={space.id}
            className="relative grid border-b border-gray-200"
            style={this.layoutGridCols()}
          >
            {this.renderLeftAssignmentColumns(space)}
            <div
              className={`relative pt-1 ${
                this.isDockwalkView()
                  ? "col-span-5 border-t border-gray-200 md:col-span-1 md:border-t-0"
                  : "col-span-3"
              }`}
            >
              <div
                className={`grid ${this.gridColumnClass()} absolute inset-0`}
              >
                {this.verticalDividers()}
              </div>
              <div className={`grid ${this.gridColumnClass()} relative w-full`}>
                {this.renderAll(space)}
              </div>
            </div>
          </div>
        )
      } else {
        const conflictDates = this.conflictsForSpaceDate(space)
        const hasConflict = _.values(conflictDates).includes(true)
        return (
          <div
            key={space.id}
            data-space-id={space.id}
            className="relative grid border-b border-gray-200"
            style={this.layoutGridCols()}
          >
            {this.renderLeftAssignmentColumns(space, hasConflict)}
            <div
              className={`relative pt-1 ${
                this.isDockwalkView()
                  ? "col-span-5 border-t border-gray-200 md:col-span-1 md:border-t-0"
                  : "col-span-3"
              }`}
            >
              <div
                className={`grid ${this.gridColumnClass()} absolute inset-0`}
              >
                {hasConflict ? this.renderConflicts(conflictDates) : null}
              </div>
              <div
                className={`grid ${this.gridColumnClass()} absolute inset-0`}
              >
                {this.verticalDividers()}
              </div>
              <div className={`grid ${this.gridColumnClass()} relative w-full`}>
                {this.renderAll(space)}
              </div>
            </div>
          </div>
        )
      }
    })
  }

  renderSpaceName(name, hasConflict) {
    if (hasConflict) {
      return (
        <div
          className="flex items-center space-x-2"
          data-content="There is a possible conflict with assignments in this space."
          data-container="body"
          data-toggle="popover"
          data-placement="bottom"
        >
          <i className="fa-solid fa-triangle-exclamation text-yellow" />
          <span>{name}</span>
        </div>
      )
    } else {
      return <span>{name}</span>
    }
  }

  renderConflicts(conflictDates) {
    return _.map(conflictDates, (hasConflict, date) => {
      if (hasConflict) {
        const dateIndex = this.props.visibleRange.daysArray.indexOf(date)
        let classes = "assignment-conflict"
        if (
          conflictDates[this.props.visibleRange.daysArray[dateIndex - 1]] !==
          true
        ) {
          classes += " assignment-conflict-start"
        }
        if (
          conflictDates[this.props.visibleRange.daysArray[dateIndex + 1]] !==
          true
        ) {
          classes += " assignment-conflict-end"
        }
        const start = dateIndex * 2 + 1
        const end = dateIndex * 2 + 3
        return (
          <div
            key={`conflict-${date}`}
            className={`${classes} ml-auto h-full w-full col-start-${start} col-end-${end}`}
          />
        )
      } else {
        return null
      }
    })
  }

  renderAll(space) {
    const hasAssignments = this.props.assignmentsBySpace[space.id] ? 1 : 0
    const hasNotes = this.props.spaceNotesBySpace[space.id] ? 1 : 0
    const hasPlaceholders = this.props.placeholdersBySpace[space.id] ? 1 : 0
    const populatedTypes = hasAssignments + hasNotes + hasPlaceholders

    const assignments = this.props.assignmentsBySpace[space.id] || []
    const notes = this.props.spaceNotesBySpace[space.id] || []
    const placeholders = this.props.placeholdersBySpace[space.id] || []
    const assignmentOrder = {}
    let allObjects = assignments.concat(...notes, ...placeholders)

    if (populatedTypes > 1) {
      allObjects = _.sortBy(allObjects, (obj) => {
        switch (obj.type) {
          case "assignment":
            return obj.start_night
          default:
            return obj.start_date
        }
      })
    }

    return _.map(allObjects, (obj) => {
      switch (obj.type) {
        case "assignment":
          return this.renderAssignment(obj, assignmentOrder, space.id)
        default:
          return this.renderPlaceholder(obj, assignmentOrder, space.id)
      }
    })
  }

  renderAssignment(assignment, assignmentOrder, spaceId) {
    if (this.shouldSkipAssignmentRender(assignment)) return null

    const forStart = this.getClassAndColumnForStart(assignment)
    const forEnd = this.getClassAndColumnForEnd(assignment)
    const isLinearAssignment =
      isPresent(assignment.bow_location) && isPresent(assignment.stern_location)
    const spaceGroup = _.find(
      this.props.spacesBySpaceGroups,
      (group) => group.id && _.find(group.spaces, { id: spaceId })
    )

    let gridOrder
    if (isLinearAssignment) {
      gridOrder = this.gridOrderForLinearDockReservation(
        assignment.bow_location,
        assignment.stern_location
      )
    } else {
      gridOrder = this.gridOrderForReservation(
        assignmentOrder,
        forStart.column,
        forEnd.column
      )
    }
    let classes = `assignment-base ${forStart.class} ${forEnd.class} col-start-${forStart.column} col-end-${forEnd.column} truncate mb-1`
    if (assignment.checked_in_at && !assignment.checked_out_at) {
      if (isPresent(assignment.reservationLabel)) {
        classes += ` rl-bg-${assignment.reservationLabel.color}-dark`
      } else {
        classes += " bg-blue-700 border-blue-700 text-white"
      }
    } else if (assignment.checked_out_at) {
      classes += " bg-gray-200 border-gray-500 text-gray-800"
    } else {
      if (isPresent(assignment.reservationLabel)) {
        classes += ` rl-bg-${assignment.reservationLabel.color}-light`
      } else {
        classes += " bg-blue-200 border-blue-200 text-blue-900"
      }
    }
    return (
      <div
        id={assignment.reservation_id}
        key={`${assignment.reservation_id}-${assignment.start_night}`}
        className={classes}
        style={{ order: gridOrder, mixBlendMode: "multiply" }}
        onClick={() => {
          highlightSpace(spaceId)
          this.props.loadReservationForSidebar(
            assignment.reservation_id,
            null,
            { skipScroll: true }
          )
        }}
        onMouseEnter={() =>
          this.setPopover("assignment", { objects: [assignment], spaceGroup })
        }
        onMouseLeave={() =>
          this.clearPopover("assignment", { objects: [assignment], spaceGroup })
        }
      >
        {isPresent(assignment.special_request) ||
        isPresent(assignment.assignment_note) ||
        isPresent(assignment.contact_note) ? (
          <i className="fa-solid fa-bell mr-2" />
        ) : null}
        <span className="font-bold">
          {boatDisplayText(
            assignment.boat_name,
            inchesToFeet(assignment.length_overall),
            assignment.boat_type
          )}
        </span>
        <span>
          {isLinearAssignment
            ? ` (${inchesToFeet(
                Math.min(assignment.bow_location, assignment.stern_location)
              )}' - ${inchesToFeet(
                Math.max(assignment.bow_location, assignment.stern_location)
              )}')`
            : null}
        </span>{" "}
        - {assignment.contact_name}
      </div>
    )
  }

  renderPlaceholder(placeholder, assignmentOrder, spaceId) {
    const forStart = this.placeholderClassAndColumnForStart(placeholder)
    const forEnd = this.placeholderClassAndColumnForEnd(placeholder)
    const gridOrder = this.gridOrderForReservation(
      assignmentOrder,
      forStart.column,
      forEnd.column
    )
    let classes = `assignment-base ${forStart.class} ${forEnd.class} col-start-${forStart.column} col-end-${forEnd.column} truncate bs-mb-1`
    classes += ` ${this.placeholderClasses(placeholder)}`
    return (
      <div
        id={`${placeholder.type}-${placeholder.id}`}
        key={`${placeholder.type}-${placeholder.id}`}
        className={classes}
        style={{ order: gridOrder, mixBlendMode: "multiply" }}
        onMouseEnter={() =>
          this.setPopover(placeholder.type, { objects: [placeholder] })
        }
        onMouseLeave={() =>
          this.clearPopover(placeholder.type, { objects: [placeholder] })
        }
        onClick={() => {
          highlightSpace(spaceId)
          this.props.setPlaceholder(placeholder, null, { skipScroll: true })
        }}
      >
        <i
          className={`${this.placeholderIconStyle(
            placeholder
          )} bs-mr-3 ${this.placeholderIconClass(placeholder)}`}
        />
        {placeholder.note}
        {isPresent(placeholder.length_overall) &&
          `, LOA: ${placeholder.length_overall}'`}
      </div>
    )
  }

  spaceGroupDisplayType() {
    return {
      "SpaceGroup::LinearV2": "LOA",
      "SpaceGroup::Linear": "LOA",
      "SpaceGroup::Count": "COUNT",
      "SpaceGroup::SquareFootage": "SQUARE FOOT",
    }
  }
}

AssignmentsView.propTypes = {
  addSpaceStatus: PropTypes.func.isRequired,
  assignmentsBySpace: PropTypes.object.isRequired,
  dateChange: PropTypes.func.isRequired,
  loadingAvailability: PropTypes.bool,
  loadReservationForSidebar: PropTypes.func.isRequired,
  loaV2Availability: PropTypes.object,
  openAddNote: PropTypes.func.isRequired,
  openAddPlaceholder: PropTypes.func.isRequired,
  openNoteForSpace: PropTypes.func.isRequired,
  openUnassignedReservations: PropTypes.func.isRequired,
  placeholdersBySpace: PropTypes.object.isRequired,
  searchFormData: PropTypes.array.isRequired,
  selectedPlaceholder: PropTypes.object,
  selectedReservationId: PropTypes.string,
  setPlaceholder: PropTypes.func.isRequired,
  spaceNotesBySpace: PropTypes.object.isRequired,
  spacesBySpaceGroups: PropTypes.array.isRequired,
  spacesForDockwalk: PropTypes.array.isRequired,
  spaceStatusesBySpace: PropTypes.object,
  unassignedReservationsCount: PropTypes.number,
  viewType: PropTypes.string.isRequired,
  viewTypeInDays: PropTypes.number.isRequired,
  visibleRange: PropTypes.object.isRequired,
  visibleSpaces: PropTypes.string,
}

export default AssignmentsView
